import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Container,
  Card,
  CardContent,
  Fade,
  Slide,
  useTheme,
  useMediaQuery
} from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import StarIcon from '@mui/icons-material/Star';
import Link from "@mui/material/Link";

// Modern styling using sx prop and theme
const styles = {
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
      zIndex: 0,
    }
  },
  container: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  heroCard: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: { xs: 0, sm: 3 },
    margin: { xs: 0, sm: 2 },
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
    height: { xs: 200, sm: 250 },
    overflow: 'hidden',
    background: 'linear-gradient(45deg, #f0f0f0, #e0e0e0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    }
  },
  contentSection: {
    padding: { xs: 2, sm: 3 },
    background: 'rgba(255, 255, 255, 0.98)',
  },
  title: {
    fontWeight: 700,
    fontSize: { xs: '1.5rem', sm: '1.75rem' },
    lineHeight: 1.2,
    marginBottom: 1,
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
  },
  subtitle: {
    color: 'text.secondary',
    fontSize: { xs: '0.9rem', sm: '1rem' },
    lineHeight: 1.5,
    marginBottom: 3,
    textAlign: 'center',
  },
  buttonContainer: {
    display: 'flex',
    gap: 2,
    flexDirection: { xs: 'column', sm: 'row' },
    marginBottom: 2,
  },
  primaryButton: {
    flex: 1,
    minHeight: 56,
    borderRadius: 3,
    fontWeight: 600,
    fontSize: '1rem',
    textTransform: 'none',
    background: '#000',
    color: 'white',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: '#333',
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 24px rgba(0, 0, 0, 0.4)',
    },
    '&:active': {
      transform: 'translateY(0)',
    }
  },
  secondaryButton: {
    flex: 1,
    minHeight: 56,
    borderRadius: 3,
    fontWeight: 600,
    fontSize: '1rem',
    textTransform: 'none',
    background: '#FFF1F2',
    color: '#E53864',
    border: '1px solid #E53864',
    boxShadow: '0 4px 8px rgba(229, 56, 100, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: '#FDE7E9',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 16px rgba(229, 56, 100, 0.3)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '& .MuiSvgIcon-root': {
      color: '#E53864',
    }
  },
  appSection: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: { xs: 0, sm: 3 },
    margin: { xs: 0, sm: 2 },
    marginTop: { xs: 0, sm: 1 },
    padding: { xs: 3, sm: 4 },
    textAlign: 'center',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  appTitle: {
    fontWeight: 800,
    fontSize: { xs: '1.25rem', sm: '1.5rem' },
    marginBottom: 2,
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1.2,
  },
  logoContainer: {
    width: 80,
    height: 80,
    margin: '0 auto 2rem',
    borderRadius: 4,
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.1) rotate(5deg)',
    }
  },
  logo: {
    width: '70%',
    height: '70%',
    objectFit: 'contain',
    filter: 'brightness(0) invert(1)',
  },
  downloadText: {
    fontWeight: 700,
    fontSize: { xs: '1rem', sm: '1.125rem' },
    marginBottom: 2,
    color: 'text.primary',
  },
  featuresList: {
    listStyle: 'none',
    padding: 0,
    margin: '2rem 0',
    textAlign: 'left',
    maxWidth: 400,
    mx: 'auto',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 1.5,
    padding: 1,
    borderRadius: 2,
    background: 'rgba(102, 126, 234, 0.05)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'rgba(102, 126, 234, 0.1)',
      transform: 'translateX(4px)',
    }
  },
  featureIcon: {
    color: '#e55c8a',
    marginRight: 1.5,
    marginTop: 0.25,
    fontSize: '1.25rem',
  },
  featureText: {
    fontSize: { xs: '0.9rem', sm: '1rem' },
    lineHeight: 1.5,
    color: 'text.primary',
    fontWeight: 500,
  },
  storeContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    marginTop: 2,
  },
  storeLink: {
    display: 'inline-block',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    }
  },
  storeImage: {
    height: 60,
    objectFit: 'contain',

  }
};

const PromotionPage = ({
  image,
  title,
  description,
  onButtonClick,
  onPromotions,
  promotionDetails,
  merchantId,
  tableNumber,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isChinese, setIsChinese] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Detect device and language
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    setIsIOS(/iPad|iPhone|iPod/.test(ua) && !window.MSStream);
    setIsAndroid(/android/i.test(ua));
    setIsChinese((navigator.language || '').toLowerCase().includes('zh'));
    
    // Trigger animations
    setIsVisible(true);
    
    // Prevent horizontal scrolling
    document.documentElement.style.overflowX = 'hidden';
    document.body.style.overflowX = 'hidden';
    
    return () => {
      document.documentElement.style.overflowX = '';
      document.body.style.overflowX = '';
    };
  }, []);

  const handlePromotionClick = () => {
    if (promotionDetails?.promotionDeepLink) {
      const appUri = `orderbuddy://merchant/${merchantId.replace('_', '')}?table=${tableNumber}`;
      
      const openAppStore = () => {
        if (isIOS) {
          window.location.href = "https://apps.apple.com/us/app/orderbuddy-app/id1599842262";
        } else if (isAndroid) {
          window.location.href = "https://play.google.com/store/apps/details?id=au.com.orderbuddy.orderbuddyapp";
        } else {
          onPromotions();
        }
      };
      
      const clickTime = Date.now();
      window.location.href = appUri;
      
      setTimeout(() => {
        if (!document.hidden && Date.now() - clickTime < 2500) {
          openAppStore();
        }
      }, 1000);
      
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && Date.now() - clickTime < 3000) {
          openAppStore();
        }
      }, { once: true });
    } else {
      onPromotions();
    }
  };

  const renderAppStoreIcons = () => (
    <Box sx={styles.storeContainer}>
      {(isIOS || (!isIOS && !isAndroid)) && (
        <Link 
          href="https://apps.apple.com/us/app/orderbuddy-app/id1599842262" 
          target="_blank" 
          rel="noopener noreferrer" 
          sx={styles.storeLink}
        >
          <img
            src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=180x60"
            alt="Download on the App Store"
            style={styles.storeImage}
          />
        </Link>
      )}
      {(isAndroid || (!isIOS && !isAndroid)) && (
        <Link 
          href="https://play.google.com/store/apps/details?id=au.com.orderbuddy.orderbuddyapp" 
          target="_blank" 
          rel="noopener noreferrer" 
          sx={styles.storeLink}
        >
          <img
            src="/images/icons/Google_Play_Store_badge_EN.svg"
            alt="Get it on Google Play"
            style={styles.storeImage}
          />
        </Link>
      )}
    </Box>
  );

  const content = {
    appTitle: isChinese ? (
      <>ORDERBUDDY APP<br />一站式美食优惠</>
    ) : (
      <>EXCLUSIVE DISCOUNTS<br />ON <span style={{ color: '#ff6b6b' }}>ORDERBUDDY APP</span></>
    ),
    downloadSignup: isChinese ? "立刻下载 APP 和注册！" : "DOWNLOAD AND SIGN UP NOW!",
    bulletPoints: isChinese
      ? ["立刻享受折扣和更多优惠", "轻松查看订单（历史或当前）"]
      : ["Enjoy immediate exclusive discounts on your order", "Easily view your orders (past or current)"],
  };

  return (
    <Box sx={styles.root}>
      <Container maxWidth="sm" sx={styles.container}>
        <Fade in={isVisible} timeout={800}>
          <Card sx={styles.heroCard} elevation={0}>
            <Box sx={styles.imageContainer}>
              <img 
                src={image} 
                alt="Promotion" 
                style={styles.image}
                loading="lazy"
              />
            </Box>
            <Box sx={styles.contentSection}>
              <Typography sx={styles.title}>
                {title}
              </Typography>
              <Typography sx={styles.subtitle}>
                {description}
              </Typography>
              <Box sx={styles.buttonContainer}>
                <Button
                  sx={styles.primaryButton}
                  onClick={onButtonClick}
                  startIcon={<RestaurantMenuIcon fontSize="medium" />}
                  fullWidth={isMobile}
                >
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography sx={{ 
                      fontSize: '1rem', 
                      fontWeight: 700, 
                      lineHeight: 1.2,
                      color: 'white',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {isChinese ? "立即下单" : "Order Now"}
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '0.75rem', 
                      color: 'rgba(255,255,255,0.9)', 
                      lineHeight: 1.1, 
                      marginTop: 0.5,
                      fontWeight: 500,
                      textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}>
                      {isChinese ? "无优惠" : "Without promotions"}
                    </Typography>
                  </Box>
                </Button>
                <Button
                  sx={styles.secondaryButton}
                  onClick={handlePromotionClick}
                  startIcon={<LocalOfferIcon fontSize="medium" />}
                  fullWidth={isMobile}
                >
                  <Typography sx={{ 
                    fontSize: '1rem', 
                    fontWeight: 700,
                    color: '#E53864',
                    textShadow: '0 1px 2px rgba(229, 56, 100, 0.2)'
                  }}>
                    {isChinese ? "优惠活动" : "Promotions"}
                  </Typography>
                </Button>
              </Box>
            </Box>
          </Card>
        </Fade>

        <Slide in={isVisible} direction="up" timeout={1000}>
          <Box sx={styles.appSection}>
            <Typography sx={styles.appTitle}>
              {content.appTitle}
            </Typography>
            
            <Box sx={styles.logoContainer}>
              <img
                src="/images/ob-logo-small.png"
                alt="Orderbuddy App"
                // style={styles.logo}
              />
            </Box>
            
            <Typography sx={styles.downloadText}>
              {content.downloadSignup}
            </Typography>
            
            <Box component="ul" sx={styles.featuresList}>
              {content.bulletPoints.map((point, i) => (
                <Box key={i} component="li" sx={styles.featureItem}>
                  <CheckCircleIcon sx={styles.featureIcon} />
                  <Typography sx={styles.featureText}>
                    {point}
                  </Typography>
                </Box>
              ))}
            </Box>
            
            {renderAppStoreIcons()}
          </Box>
        </Slide>
      </Container>
    </Box>
  );
};

export default PromotionPage;
